import clsx from 'clsx';
import { Column } from 'primereact/column';
import {
	DataTable,
	DataTableStateEvent,
	DataTableValue,
	SortOrder,
	type DataTableExpandedRows,
	type DataTableValueArray,
} from 'primereact/datatable';
import { ProgressBar } from 'primereact/progressbar';
import { Tag } from 'primereact/tag';
import { useState } from 'react';
import { MdArrowDropDown, MdArrowRight } from 'react-icons/md';
import {
	buildPaginatorTemplate,
	PAGINATOR_CURRENT_REPORT,
	paginatorPassThrough,
} from '@/components/table/pagination/Paginator';
import type { BudgetRequestDto, GetBudgetRequestListRequest } from '../../engine/budget.types';
import RequestRowExpansionTemplate from './row-expansion/RequestRowExpansion';
import styles from './table.module.css';
import { useQuery } from '@tanstack/react-query';
import { axiosInstance } from '@/services/axios.service';
import { copyWith, formatCurrency, GenericResponse, ListResponse } from '@/common/common_utils';
import { ENDPOINTS } from '@/constants/endpoints';
import { DateHelper } from '@/common/DateHelper';
import EmptyTable from '@/components/empty/EmptyTable';

interface RequestsTableProps {
	refetchTrigger?: number;
	onActionComplete?: () => void;
}

const RequestsTable = ({ refetchTrigger, onActionComplete }: RequestsTableProps) => {
	const [expandedRows, setExpandedRows] = useState<
		DataTableExpandedRows | DataTableValueArray | undefined
	>([]);

	// sorting states
	const [sort, setSort] = useState({ sortField: '', sortOrder: 0 });

	// pagination states
	const [paginationFilter, setPaginationFilter] = useState({ Skip: 0, Take: 10 });

	const makeListRequest = (): GetBudgetRequestListRequest => {
		return {
			Skip: paginationFilter.Skip,
			Take: paginationFilter.Take,
			SortField: sort.sortField,
			SortOrder: sort.sortOrder,
		};
	};

	const budgetRequestsQ = useQuery({
		queryKey: ['Budget.GetBudgetRequestList', paginationFilter, sort, refetchTrigger],
		queryFn: async () => {
			const result = await axiosInstance.post<GenericResponse<ListResponse<BudgetRequestDto>>>(
				ENDPOINTS['/api/Budget/GetBudgetRequestList'],
				makeListRequest()
			);
			return result.data.Data;
		},
	});


	const onRowsChange = (rows: number) => {
		setPaginationFilter(copyWith(paginationFilter, { Skip: 0, Take: rows }));
	};

	const isRowExpanded = (rowData: DataTableValue) =>
		Array.isArray(expandedRows)
			? expandedRows.some((row) => row === rowData)
			: false;

	const renderEmptyState = (state?: 'noData' | 'error') => {
		switch (state) {
			case 'noData':
				return EmptyTable({
					title: 'No budget requests found',
					src: '/images/svg/no-result.svg',
				});
			case 'error':
				return EmptyTable({
					title: 'Something went wrong',
					src: '/images/svg/no-filter.svg',
				});
			default:
				return null;
		}
	};

	const formatRequestId = (id: number) => {
		return `XB-${id.toString().padStart(10, '0')}`;
	};

	const emptyState = 'noData';

	return (
		<div className="relative">
			{budgetRequestsQ.isLoading && (
				<div className="absolute top-0 w-full rounded-t h-2 z-50">
					<ProgressBar
						className={styles.progressBar}
						mode="indeterminate"
						color="#0080FF"
						pt={{
							container: {
								className: 'bg-primary500',
							},
						}}
					/>
				</div>
			)}
			<DataTable
				value={budgetRequestsQ.data?.Items}
				className={styles.customDatatable}
				tableStyle={{ minWidth: '50rem', width: '100%' }}
				pt={{ paginator: paginatorPassThrough }}
				paginatorTemplate={buildPaginatorTemplate(onRowsChange)}
				currentPageReportTemplate={PAGINATOR_CURRENT_REPORT}
				emptyMessage={budgetRequestsQ.isLoading ? null : renderEmptyState(emptyState)}
				expandedRows={expandedRows}
				onRowToggle={(e) => {
					// Single row expansion: replace all expanded rows with just the toggled row
					setExpandedRows(isRowExpanded(e.data) ? [] : [e.data]);
				}}
				rowExpansionTemplate={(rowData: BudgetRequestDto) => (
					<RequestRowExpansionTemplate
						data={rowData}
						onActionComplete={() => {
							budgetRequestsQ.refetch();
							setExpandedRows([]);
							onActionComplete?.();
						}}
					/>
				)}
				loading={budgetRequestsQ.isLoading}
				dataKey="Id"
				paginator={(budgetRequestsQ.data?.Items?.length ?? 0) > 0}
				first={paginationFilter.Skip}
				rows={paginationFilter.Take}
				lazy
				totalRecords={budgetRequestsQ.data?.TotalRecords}
				rowsPerPageOptions={[5, 10, 25, 50]}
				onPage={(event: DataTableStateEvent) => {
					setPaginationFilter(copyWith(paginationFilter, { Skip: event.first }));
				}}
				sortField={sort.sortField}
				sortOrder={sort.sortOrder as SortOrder}
				onSort={(e: DataTableStateEvent) => {
					setSort({
						sortField: e.sortField,
						sortOrder: e.sortOrder!,
					});
				}}
			>
				<Column
					header=""
					body={(rowData) =>
						budgetRequestsQ.isLoading ? null : (
							<button
								type="button"
								onClick={() => {
									// Single row expansion: replace all expanded rows with just the clicked row
									setExpandedRows(isRowExpanded(rowData) ? [] : [rowData]);
								}}
								className={styles.expandToggle}
							>
								{isRowExpanded(rowData) ? (
									<MdArrowDropDown
										size={24}
										className="text-tertiary"
									/>
								) : (
									<MdArrowRight
										size={24}
										className="text-black"
									/>
								)}
							</button>
						)
					}
					style={{ width: '3rem', textAlign: 'center' }}
				/>
				<Column
					field="Id"
					header="Request ID"
					sortable
					body={(request: BudgetRequestDto) => formatRequestId(request.Id)}
				/>
				<Column
					field="DateRequested"
					header="Date"
					sortable
					body={(request: BudgetRequestDto) =>
						request.DateRequested ? DateHelper.formatDate(request.DateRequested) : ''
					}
				/>
				<Column
					field="Amount"
					header="Amount"
					sortable
					body={(request: BudgetRequestDto) => {
						return <span>{formatCurrency(request.Amount ?? 0)}</span>;
					}}
				/>
				<Column
					field="Status"
					header="Status"
					sortable
					headerClassName="tablet:!w-[200px]"
					bodyClassName="tablet:!w-[200px]"
					body={(request: BudgetRequestDto) => {
						return (
							<Tag
								value={request.Status}
								className={clsx({
									[styles.approvedTag]:
										request.Status === 'Approved',
									[styles.declinedTag]:
										request.Status === 'Declined',
									[styles.waitingTag]:
										request.Status === 'Waiting for Approval',
								})}
							/>
						);
					}}
				/>
			</DataTable>
		</div>
	);
};

export default RequestsTable;
